import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FaBullseye, FaEye, FaTrophy, FaUsers, FaGraduationCap, FaAward } from 'react-icons/fa';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us - EduConsult</title>
        <meta name="description" content="Learn about EduConsult's mission to provide expert college admission guidance and help students achieve their academic dreams." />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-pattern pt-32 pb-20 md:pt-40 md:pb-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              About <span className="gradient-text">EduConsult</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              We are dedicated to helping students navigate the complex college admission process 
              and find the perfect institution to achieve their academic and career goals.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Founded in 2010, EduConsult emerged from a simple yet powerful vision: to democratize 
                access to quality education by providing expert guidance to students from all backgrounds.
              </p>
              <p className="text-gray-600 mb-4 leading-relaxed">
                What started as a small team of education consultants has grown into a comprehensive 
                platform serving thousands of students across India. Our team of experienced counselors 
                brings together decades of expertise in college admissions, career planning, and 
                educational consulting.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Today, we pride ourselves on our 98% success rate and the lasting relationships we've 
                built with students, parents, and educational institutions nationwide.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding gradient-bg">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl mb-6">
                <FaBullseye className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To empower students with personalized guidance, comprehensive resources, and expert 
                support that enables them to make informed decisions about their educational future 
                and successfully navigate the college admission process.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-secondary-600 to-secondary-700 rounded-xl mb-6">
                <FaEye className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To become India's most trusted educational consultancy, recognized for transforming 
                lives through education by connecting every aspiring student with opportunities 
                that match their potential, regardless of their background or location.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Our <span className="gradient-text">Achievements</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Milestones that reflect our commitment to student success
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FaUsers,
                number: '5000+',
                title: 'Students Guided',
                description: 'Successfully placed in top colleges'
              },
              {
                icon: FaGraduationCap,
                number: '500+',
                title: 'Partner Colleges',
                description: 'Across India and abroad'
              },
              {
                icon: FaAward,
                number: '98%',
                title: 'Success Rate',
                description: 'In college admissions'
              },
              {
                icon: FaTrophy,
                number: '15+',
                title: 'Years of Excellence',
                description: 'In educational consulting'
              },
              {
                icon: FaUsers,
                number: '50+',
                title: 'Expert Counselors',
                description: 'With diverse specializations'
              },
              {
                icon: FaAward,
                number: '25+',
                title: 'Awards Won',
                description: 'For educational excellence'
              }
            ].map((achievement, index) => (
              <div key={index} className="text-center p-6 rounded-xl hover:bg-primary-50 transition-all duration-300 group">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  <achievement.icon className="text-white text-3xl" />
                </div>
                <div className="text-4xl font-bold gradient-text mb-2">{achievement.number}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{achievement.title}</h3>
                <p className="text-gray-600">{achievement.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Trust Us */}
      <section className="section-padding gradient-bg">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Why Students <span className="gradient-text">Trust Us</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Personalized one-on-one counseling',
              'Expert guidance from experienced professionals',
              'Comprehensive college database',
              'Application support and documentation help',
              'Entrance exam preparation guidance',
              'Scholarship and financial aid assistance',
              'Career counseling and future planning',
              'Post-admission support and mentoring'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow">
                <div className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-1">
                  <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                </div>
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Let our expert counselors guide you towards your dream college
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register" className="btn bg-white text-primary-600 hover:bg-gray-100">
              Register for Consultation
            </Link>
            <Link to="/team" className="btn bg-transparent border-2 border-white hover:bg-white/10">
              Meet Our Team
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
